"use client";

import React, { useState, useEffect } from "react";
import { Menu, ChevronDown, ChevronRight, Phone, ClipboardList, MessageCircle, MessageSquare } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";

// Language hook to sync language state with localStorage and window events
function useLanguage() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "vi" || stored === "en") {
        setLang(stored);
      }
    } catch {
      // Ignore localStorage access errors
    }
    const handleCustomChange = (e: Event) => {
      const detail = (e as CustomEvent<{
        lang?: string;
      }>).detail;
      if (detail?.lang === "vi" || detail?.lang === "en") {
        setLang(detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const changeLang = (nextLang: "vi" | "en") => {
    setLang(nextLang);
    try {
      localStorage.setItem("roboto_lang", nextLang);
    } catch {
      // Ignore localStorage access errors
    }
    window.dispatchEvent(new CustomEvent("roboto-lang-change", {
      detail: {
        lang: nextLang
      }
    }));
  };
  return {
    lang,
    changeLang
  };
}

// Vietnamese-to-English label lookup for direct title translation (e.g. query-string/numeric hrefs)
const VI_TO_EN_LABEL_LOOKUP: Record<string, string> = {
  "BÁO GIÁ": "Pricing",
  "BÁO GIÁ ĐỐI TÁC": "Pricing",
  "TRANG CHỦ": "Home",
  "CHƯƠNG TRÌNH": "Programs",
  "LỘ TRÌNH HỌC": "Curriculum",
  "LỘ TRÌNH ROBOTICS": "Robotics Pathway",
  "ROBOTICS 4–6 TUỔI": "Robotics Ages 4–6",
  "ROBOTICS 7+ TUỔI": "Robotics Ages 7+",
  "ENJOY AI NÂNG CAO": "Enjoy AI Advanced Prep",
  "ENJOY AI (NÂNG CAO)": "Enjoy AI Advanced Prep",
  "ENJOY AI (Nâng cao)": "Enjoy AI Advanced Prep",
  "XEM TOÀN BỘ LỘ TRÌNH": "View Full Pathway",
  "Xem toàn bộ lộ trình": "View Full Pathway",
  "DỰ ÁN": "Projects",
  "DỰ ÁN & CUỘC THI": "Projects & Competitions",
  "CUỘC THI": "Competitions",
  "TÀI NGUYÊN": "Parent Guide",
  "HỆ THỐNG CƠ SỞ": "Centers",
  "VỀ ROBOTO": "About Us",
  "THÀNH TÍCH": "Achievements",
  "GIÁO VIÊN": "Teachers",
  "ĐĂNG KÝ TRẢI NGHIỆM": "Free Trial"
};

// English lookup keyed by the last segment of item's href
const ENGLISH_MENU_LOOKUP: Record<string, string> = {
  programs: "Programs",
  programspage: "Programs",
  curriculum: "Curriculum",
  learningpathcurriculumpage: "Curriculum",
  competitions: "Competitions",
  projectscompetitionspage: "Competitions",
  projects: "Projects",
  achievements: "Achievements",
  studentachievementsstoriespage: "Achievements",
  teachers: "Teachers",
  teacherspage: "Teachers",
  centers: "Centers",
  facilitiescenterspage: "Centers",
  "parent-guide": "Parent Guide",
  parentguide: "Parent Guide",
  parentguideresourcespage: "Parent Guide",
  "about-us": "About Us",
  aboutus: "About Us",
  aboutuspage: "About Us",
  pricing: "Pricing",
  partnerpricingpage: "Pricing",
  "robotics-learning-pathway": "Robotics Pathway",
  roboticslearningpathwaypage: "Robotics Pathway",
  "robotics-course-d1": "D1 — Junior Engineer",
  "robotics-course-d2": "D2 — City Builder",
  "robotics-course-d3": "D3 — Marvel Machines",
  "robotics-course-d4": "D4 — Little Motors",
  "robotics-course-hp1": "HP1 — Junior Mechanical Engineer",
  "robotics-course-hp2": "HP2 — Sensing Robots",
  "robotics-course-hp3": "HP3 — First Robot Car",
  "robotics-course-hp4": "HP4 — Smart Robot Car",
  "robotics-course-hp5": "HP5 — Self-Driving City",
  "robotics-course-hp6": "HP6 — Mission Robots"
};
function getLocalizedLabel(item: MenuItem, lang: "vi" | "en"): string {
  if (lang === "vi") return item.label;

  // 1. Primary check: match exact Vietnamese label string or normalized uppercase
  const rawLabel = (item.label || "").trim();
  const upperLabel = rawLabel.toUpperCase();
  if (VI_TO_EN_LABEL_LOOKUP[rawLabel]) return VI_TO_EN_LABEL_LOOKUP[rawLabel];
  if (VI_TO_EN_LABEL_LOOKUP[upperLabel]) return VI_TO_EN_LABEL_LOOKUP[upperLabel];

  // 2. Fallback check: lookup by clean URL slug
  if (!item.href) return item.label;
  const cleanPath = item.href.split("?")[0].split("#")[0].replace(/\/+$/, "");
  const lastSegment = cleanPath.split("/").pop() || "";
  const key = lastSegment.toLowerCase();
  return ENGLISH_MENU_LOOKUP[key] || ENGLISH_MENU_LOOKUP[key.replace(/[-_]/g, "")] || item.label;
}

// Extract path key to map specific menu links to their rich mega-menus
function getMegaMenuKey(href?: string): "projects" | "parent-guide" | null {
  if (!href) return null;
  const withoutHashQuery = href.split("?")[0].split("#")[0];
  const pathOnly = withoutHashQuery.replace(/^https?:\/\/[^/]+/, "");
  const cleanPath = pathOnly.replace(/\/+$/, "");
  const segment = (cleanPath.split("/").filter(Boolean).pop() || "").toLowerCase();
  if (segment === "projects" || segment === "projectscompetitionspage") return "projects";
  if (segment === "parent-guide" || segment === "parent_guide" || segment === "parentguideresourcespage") return "parent-guide";
  return null;
}

// Reusable language switcher component
function LanguageSwitcher({
  lang,
  onChange
}: {
  lang: "vi" | "en";
  onChange: (l: "vi" | "en") => void;
}) {
  return <div role="group" aria-label="Language selection" className="inline-flex items-center rounded-lg bg-background/90 p-0.5 border border-border text-xs font-semibold shadow-2xs">
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange("vi")} className={`h-6 px-2 py-0.5 text-[11px] font-bold rounded-md transition-all ${lang === "vi" ? "bg-primary text-primary-foreground shadow-xs hover:bg-primary hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`} aria-pressed={lang === "vi"} aria-label="Tiếng Việt">
        VI
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange("en")} className={`h-6 px-2 py-0.5 text-[11px] font-bold rounded-md transition-all ${lang === "en" ? "bg-primary text-primary-foreground shadow-xs hover:bg-primary hover:text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`} aria-pressed={lang === "en"} aria-label="English">
        EN
      </Button>
    </div>;
}

// Recursive submenu item component for default desktop dropdowns
function DesktopSubmenuItems({
  items,
  lang
}: {
  items: MenuItem[];
  lang: "vi" | "en";
}) {
  return <ul className="flex flex-col gap-1 min-w-[210px] p-1">
      {items.map((child, i) => {
      const localizedLabel = getLocalizedLabel(child, lang);
      return <li key={child.id} data-index={i} className="relative group/sub">
            {child.children && child.children.length > 0 ? <div className="flex flex-col">
                <NavigationMenuLink asChild>
                  <Link to={child.href || "#"} className="flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors min-w-0">
                    <span className="whitespace-nowrap truncate">{localizedLabel}</span>
                    <ChevronRight className="size-3.5 text-muted-foreground shrink-0 ml-1.5" />
                  </Link>
                </NavigationMenuLink>
                <div className="pl-3 border-l border-border mt-1">
                  <DesktopSubmenuItems items={child.children} lang={lang} />
                </div>
              </div> : <NavigationMenuLink asChild>
                <Link to={child.href || "#"} className="block rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors min-w-0">
                  <span className="whitespace-nowrap">{localizedLabel}</span>
                </Link>
              </NavigationMenuLink>}
          </li>;
    })}
    </ul>;
}

/* =========================================================================
   MEGA-MENU PREVIEW PANELS (DESKTOP ONLY)
   ========================================================================= */

// 1. Projects & Competitions Mega-menu (2 Columns)
function ProjectsColConsumer({
  lang
}: {
  lang: "vi" | "en";
}) {
  const {
    posts,
    loading
  } = usePosts();
  const fallbackProjects = [{
    nameVi: "Robot phân loại rác thông minh AI",
    nameEn: "AI Waste Sorting Robot",
    href: "/ProjectsCompetitionsPage#student-projects-gallery"
  }, {
    nameVi: "Hệ thống nhà kính tự động IoT",
    nameEn: "Smart IoT Automated Greenhouse",
    href: "/ProjectsCompetitionsPage#student-projects-gallery"
  }, {
    nameVi: "Xe tự hành tránh chướng ngại vật LiDAR",
    nameEn: "Autonomous LiDAR Rover",
    href: "/ProjectsCompetitionsPage#student-projects-gallery"
  }];
  const hasPosts = !loading && posts && posts.length > 0;
  const list = hasPosts ? posts.slice(0, 3) : [];
  return <div className="flex flex-col gap-2 min-w-0">
      <div className="px-2.5 py-2 border-b border-border flex items-center justify-between bg-muted/30 rounded-t-md">
        <span className="text-xs font-bold uppercase tracking-wider text-foreground">
          {lang === "vi" ? "Dự Án" : "Projects"}
        </span>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-secondary/15 text-secondary border border-secondary/30">
          {lang === "vi" ? "Học viên ROBOTO" : "Student Builds"}
        </span>
      </div>

      <ul className="flex flex-col gap-1 min-h-[140px] mt-1">
        {list.length > 0 ? list.map((p, i) => {
        const name = (p as any).acf?.project_name || p.customFields?.project_name || p.title;
        return <li key={p.id || i} data-index={i}>
                  <NavigationMenuLink asChild>
                    <Link to={p.link || "/ProjectsCompetitionsPage#student-projects-gallery"} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/70 transition-colors group/row min-w-0">
                      <span className="line-clamp-1 group-hover/row:text-primary transition-colors min-w-0">
                        {name}
                      </span>
                      <ChevronRight className="size-3.5 text-secondary opacity-0 group-hover/row:opacity-100 group-hover/row:translate-x-0.5 transition-all shrink-0 ml-2" />
                    </Link>
                  </NavigationMenuLink>
                </li>;
      }) : fallbackProjects.map((item, i) => <li key={item.nameVi} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/70 transition-colors group/row min-w-0">
                    <span className="line-clamp-1 group-hover/row:text-primary transition-colors min-w-0">
                      {lang === "vi" ? item.nameVi : item.nameEn}
                    </span>
                    <ChevronRight className="size-3.5 text-secondary opacity-0 group-hover/row:opacity-100 group-hover/row:translate-x-0.5 transition-all shrink-0 ml-2" />
                  </Link>
                </NavigationMenuLink>
              </li>)}
      </ul>

      <div className="pt-2 border-t border-border px-1">
        <NavigationMenuLink asChild>
          <Link to="#" className="text-xs font-semibold text-primary hover:text-primary/90 inline-flex items-center gap-1.5 py-1.5 px-2.5 rounded-md hover:bg-primary/10 transition-colors">
            <span>{lang === "vi" ? "Xem tất cả dự án" : "View all projects"}</span>
            <ChevronRight className="size-3.5" />
          </Link>
        </NavigationMenuLink>
      </div>
    </div>;
}
function ProjectsCompetitionsMenuPanel({
  lang
}: {
  lang: "vi" | "en";
}) {
  const tournaments = [{
    name: "FIRST LEGO League",
    href: "/ProjectsCompetitionsPage"
  }, {
    name: "World Robot Olympiad",
    href: "/ProjectsCompetitionsPage"
  }, {
    name: "National VEX Robotics Championship",
    href: "/ProjectsCompetitionsPage"
  }];
  return <div className="grid grid-cols-2 gap-4 min-w-[560px] w-[560px] p-2.5">
      {/* Column 1: Projects (PostsProvider) */}
      <PostsProvider wp_query={{
      post_type: "project",
      per_page: 3,
      status: ["publish"]
    }}>
        <ProjectsColConsumer lang={lang} />
      </PostsProvider>

      {/* Column 2: Competitions (Static) */}
      <div className="flex flex-col gap-2 pl-4 border-l border-border min-w-0">
        <div className="px-2.5 py-2 border-b border-border flex items-center justify-between bg-muted/30 rounded-t-md">
          <span className="text-xs font-bold uppercase tracking-wider text-foreground">
            {lang === "vi" ? "Cuộc Thi" : "Competitions"}
          </span>
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-secondary/15 text-secondary border border-secondary/30">
            {lang === "vi" ? "Đấu trường quốc tế" : "Global Arenas"}
          </span>
        </div>

        <ul className="flex flex-col gap-1 min-h-[140px] mt-1">
          {tournaments.map((tour, i) => <li key={tour.name} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={tour.href} className="flex items-center justify-between rounded-lg px-3 py-1.5 text-foreground hover:bg-muted/70 transition-colors group/row min-w-0">
                  <span className="text-sm font-semibold group-hover/row:text-primary transition-colors min-w-0 truncate">
                    {tour.name}
                  </span>
                  <ChevronRight className="size-3.5 text-secondary opacity-0 group-hover/row:opacity-100 group-hover/row:translate-x-0.5 transition-all shrink-0 ml-2" />
                </Link>
              </NavigationMenuLink>
            </li>)}
        </ul>

        <div className="pt-2 border-t border-border px-1">
          <NavigationMenuLink asChild>
            <Link to="/ProjectsCompetitionsPage" className="text-xs font-semibold text-primary hover:text-primary/90 inline-flex items-center gap-1.5 py-1.5 px-2.5 rounded-md hover:bg-primary/10 transition-colors">
              <span>
                {lang === "vi" ? "Xem tất cả giải đấu" : "View all competitions"}
              </span>
              <ChevronRight className="size-3.5" />
            </Link>
          </NavigationMenuLink>
        </div>
      </div>
    </div>;
}

// 2. Parent Guide Mega-menu
function ParentGuideMenuPanel({
  lang
}: {
  lang: "vi" | "en";
}) {
  const guideCategories = [{
    vi: "Định hướng Lộ trình",
    en: "Roadmap Orientation"
  }, {
    vi: "Phương pháp STEM",
    en: "STEM Methodology"
  }, {
    vi: "Đấu trường & Giải đấu",
    en: "Arenas & Competitions"
  }, {
    vi: "Đồng hành cùng Con",
    en: "Partnering with Kids"
  }];
  return <div className="flex flex-col gap-2 min-w-[380px] w-[380px] p-2.5">
      <div className="px-2.5 py-2 border-b border-border flex items-center justify-between bg-muted/30 rounded-t-md">
        <span className="text-xs font-bold uppercase tracking-wider text-foreground">
          {lang === "vi" ? "Cẩm nang phụ huynh" : "Parent Resources"}
        </span>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-md bg-secondary/15 text-secondary border border-secondary/30">
          {lang === "vi" ? "Kiến thức hữu ích" : "Helpful Guides"}
        </span>
      </div>

      <ul className="flex flex-col gap-1 mt-1">
        {guideCategories.map((cat, i) => <li key={cat.vi} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to="/ParentGuideResourcesPage" className="flex items-center justify-between rounded-lg px-3 py-1.5 text-foreground hover:bg-muted/70 transition-colors group/row min-w-0">
                <span className="text-sm font-semibold group-hover/row:text-primary transition-colors min-w-0 truncate">
                  {lang === "vi" ? cat.vi : cat.en}
                </span>
                <ChevronRight className="size-3.5 text-secondary opacity-0 group-hover/row:opacity-100 group-hover/row:translate-x-0.5 transition-all shrink-0 ml-2" />
              </Link>
            </NavigationMenuLink>
          </li>)}
      </ul>

      <div className="pt-2 border-t border-border flex items-center justify-between px-1">
        <NavigationMenuLink asChild>
          <Link to="/ParentGuideResourcesPage" className="text-xs font-semibold text-primary hover:text-primary/90 inline-flex items-center gap-1.5 py-1.5 px-2.5 rounded-md hover:bg-primary/10 transition-colors">
            <span>
              {lang === "vi" ? "Xem tất cả cẩm nang" : "View all guides"}
            </span>
            <ChevronRight className="size-3.5" />
          </Link>
        </NavigationMenuLink>
      </div>
    </div>;
}

// Desktop Menu Consumer (must be rendered inside MenuProvider)
function DesktopNavConsumer({
  lang
}: {
  lang: "vi" | "en";
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2 shrink-0">
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-28 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="shrink-0 max-w-full">
      <NavigationMenuList className="gap-1 xl:gap-2 flex-nowrap shrink-0">
        {menuItems.map((item, i) => {
        const localizedLabel = getLocalizedLabel(item, lang);
        const megaKey = getMegaMenuKey(item.href);
        const hasChildren = item.children && item.children.length > 0;
        return <NavigationMenuItem key={item.id} data-index={i} className="shrink-0">
              {hasChildren ? <>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-muted/80 text-foreground font-serif text-sm font-semibold tracking-normal px-3 xl:px-3.5 py-2 rounded-lg transition-colors whitespace-nowrap shrink-0">
                    <span className="whitespace-nowrap">{localizedLabel}</span>
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="p-2 border border-border bg-popover/98 backdrop-blur-md text-popover-foreground shadow-xl rounded-xl">
                    <DesktopSubmenuItems items={item.children} lang={lang} />
                  </NavigationMenuContent>
                </> : megaKey ? <>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-muted/80 text-foreground font-serif text-sm font-semibold tracking-normal px-3 xl:px-3.5 py-2 rounded-lg transition-colors whitespace-nowrap shrink-0">
                    <span className="whitespace-nowrap">{localizedLabel}</span>
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="p-2 border border-border bg-popover/98 backdrop-blur-md text-popover-foreground shadow-xl rounded-xl">
                    {megaKey === "projects" && <ProjectsCompetitionsMenuPanel lang={lang} />}
                    {megaKey === "parent-guide" && <ParentGuideMenuPanel lang={lang} />}
                  </NavigationMenuContent>
                </> : <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="inline-flex h-9 items-center justify-center rounded-lg px-3 xl:px-3.5 py-2 font-serif text-sm font-semibold tracking-normal text-foreground transition-colors hover:bg-muted/80 hover:text-primary whitespace-nowrap shrink-0">
                    <span className="whitespace-nowrap">{localizedLabel}</span>
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}

// Recursive mobile item for deep hierarchy
function MobileSubmenuItems({
  items,
  lang,
  depth = 1
}: {
  items: MenuItem[];
  lang: "vi" | "en";
  depth?: number;
}) {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const toggleItem = (id: string) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <ul className={`flex flex-col gap-1 ${depth > 1 ? "pl-3 border-l-2 border-secondary/30 mt-1" : ""}`}>
      {items.map((item, i) => {
      const hasKids = item.children && item.children.length > 0;
      const isOpen = !!openItems[item.id];
      const localizedLabel = getLocalizedLabel(item, lang);
      const isEnjoyAi = (item.href || "").toLowerCase().includes("enjoy") || (item.label || "").toUpperCase().includes("ENJOY AI");
      return <React.Fragment key={item.id}>
            {isEnjoyAi && <li className="my-1.5 pt-1.5 border-t border-border flex items-center justify-between px-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-secondary font-bold">
                  {lang === "vi" ? "Giai đoạn nâng cao" : "Advanced Stage"}
                </span>
                <span className="text-[9px] px-1.5 py-0.5 text-muted-foreground bg-muted rounded font-mono">
                  {lang === "vi" ? "Độc lập · Không phải HP7" : "Special Prep · Not HP7"}
                </span>
              </li>}
            <li data-index={i} className="flex flex-col">
              <div className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-muted/50 transition-colors">
                <SheetClose asChild>
                  <Link to={item.href || "#"} className="text-sm font-semibold text-foreground hover:text-primary transition-colors flex-1">
                    {localizedLabel}
                  </Link>
                </SheetClose>
                {hasKids && <Button variant="ghost" size="icon" className="size-8 text-muted-foreground hover:text-foreground" onClick={() => toggleItem(item.id)} aria-label={`Toggle ${localizedLabel} submenu`}>
                    <ChevronDown className={`size-4 transition-transform duration-200 ${isOpen ? "rotate-180 text-secondary" : ""}`} />
                  </Button>}
              </div>
              {hasKids && isOpen && item.children && <MobileSubmenuItems items={item.children} lang={lang} depth={depth + 1} />}
            </li>
          </React.Fragment>;
    })}
    </ul>;
}

// Mobile Menu Consumer (must be rendered inside MenuProvider inside Sheet)
function MobileNavConsumer({
  lang
}: {
  lang: "vi" | "en";
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 py-4">
        <div className="h-6 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-1/2 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="py-1 flex flex-col gap-1">
      <MobileSubmenuItems items={menuItems} lang={lang} />
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const {
    lang,
    changeLang
  } = useLanguage();

  // Monitor scroll for visual border and background elevation past 60px
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll handler for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 90;
            const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            window.scrollTo({
              top,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const ctaButtonText = lang === "vi" ? "ĐĂNG KÝ TRẢI NGHIỆM" : "Book a Free Trial";
  return <>
      <section id="header" data-nav="light" className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "bg-background/95 backdrop-blur-xl border-b border-border shadow-md" : "bg-background/90 backdrop-blur-md border-b border-border/70"}`}>
        {/* Row 1: Slim Utility Bar */}
        <div className="relative w-full border-b border-border/80 bg-muted/50 dark:bg-muted/30 transition-colors">
          <div className="mx-auto flex h-9 max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-16">
            {/* Left Utility telemetry note */}
            <div className="flex items-center gap-2">
              <span className="size-1.5 rounded-sm bg-secondary animate-pulse" />
              <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground hidden md:inline">
                ROBOTO STEM ACADEMY • LAB SPEC
              </span>
            </div>

            {/* Right cluster: hotline and language */}
            <div className="flex items-center gap-3">
              <a href="tel:0346386986" className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground hover:text-primary transition-colors py-0.5 px-2 rounded-md hover:bg-background/60" aria-label="Hotline: 0346 386 986">
                <Phone className="size-3 text-secondary shrink-0" />
                <span className="font-mono text-xs font-bold text-foreground">
                  <span className="hidden sm:inline font-sans text-muted-foreground mr-1">Hotline:</span>
                  0346 386 986
                </span>
              </a>
              <span className="h-3.5 w-px bg-border" aria-hidden="true" />
              <LanguageSwitcher lang={lang} onChange={changeLang} />
            </div>
          </div>

          {/* Refined Horizontal Circuit-Line SVG Decoration with deliberate lab blueprint layout */}
          <div className={`absolute inset-x-0 -bottom-[1px] h-2.5 overflow-hidden pointer-events-none transition-opacity duration-300 ${scrolled ? "opacity-45" : "opacity-30"}`} aria-hidden="true">
            <svg className="w-full h-2.5 stroke-secondary" viewBox="0 0 1440 10" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <path d="M0 5h140l5-3h90l5 3h180l6 3h80l6-3h220l5-3h90l5 3h160l6 3h70l6-3h180l5-3h90l5 3h100" strokeWidth="1.25" />
              <rect x="233" y="1" width="3" height="3" className="fill-secondary stroke-none" />
              <rect x="504" y="7" width="3" height="3" className="fill-secondary stroke-none" />
              <rect x="828" y="1" width="3" height="3" className="fill-secondary stroke-none" />
              <rect x="1134" y="7" width="3" height="3" className="fill-secondary stroke-none" />
              <rect x="1398" y="1" width="3" height="3" className="fill-secondary stroke-none" />
            </svg>
          </div>
        </div>

        {/* Row 2: Main Navigation Bar */}
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-16 gap-4">
          {/* Brand Logo - Standalone Link wrapper without sibling elements */}
          <div className="flex items-center shrink-0">
            <Link to="/">
              <WvcLogo className="!w-[300px] !h-[90px] [&_svg]:!w-[300px] [&_svg]:!h-[90px]" />
            </Link>
          </div>

          {/* Desktop Navigation immediately right of logo */}
          <div className="hidden lg:flex items-center mr-auto ml-6 shrink-0">
            <MenuProvider menu_id="43" className="flex items-center shrink-0">
              <DesktopNavConsumer lang={lang} />
            </MenuProvider>
          </div>

          {/* Right Action Cluster: CTA and Mobile Trigger */}
          <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
            {/* Primary CTA: ĐĂNG KÝ TRẢI NGHIỆM */}
            <Button asChild className="hidden sm:inline-flex bg-accent text-accent-foreground font-serif font-bold rounded-lg px-4 xl:px-6 py-2.5 shadow-xs hover:bg-accent/95 hover:shadow-md hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 shrink-0 text-xs xl:text-sm whitespace-nowrap">
              <Link to="/FreeTrialContactPage">{ctaButtonText}</Link>
            </Button>

            {/* Mobile Navigation Trigger & Slide-over Drawer */}
            <div className="lg:hidden">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="size-10 rounded-lg border-border text-foreground hover:bg-muted/80 shadow-2xs" aria-label="Open navigation menu">
                    <Menu className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-sm bg-background border-l border-border p-6 flex flex-col justify-between shadow-2xl">
                  <div className="flex flex-col gap-4">
                    <SheetHeader className="p-0 text-left border-b border-border/80 pb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="size-2 rounded-sm bg-secondary" />
                        <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                          ROBOTO INCUBATOR
                        </span>
                      </div>
                      <SheetTitle className="text-xl font-serif font-bold text-foreground">
                        {lang === "vi" ? "Menu Điều Hướng" : "Navigation Menu"}
                      </SheetTitle>
                      <SheetDescription className="text-xs text-muted-foreground mt-1">
                        {lang === "vi" ? "Khám phá các khóa học robotics, lộ trình và phòng lab công nghệ." : "Explore robotics courses, curriculum, and technology labs."}
                      </SheetDescription>
                    </SheetHeader>

                    {/* Mobile Language Switcher Bar */}
                    <div className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-muted/40 border border-border">
                      <span className="text-xs font-semibold text-foreground">
                        {lang === "vi" ? "Ngôn ngữ / Language" : "Language / Ngôn ngữ"}
                      </span>
                      <LanguageSwitcher lang={lang} onChange={changeLang} />
                    </div>

                    {/* Mobile Navigation Menu Tree */}
                    <div className="mt-1 overflow-y-auto max-h-[calc(100vh-340px)] pr-1">
                      <MenuProvider menu_id="43" className="mobile-nav-container">
                        <MobileNavConsumer lang={lang} />
                      </MenuProvider>
                    </div>
                  </div>

                  {/* Mobile Drawer Bottom Cluster */}
                  <div className="pt-4 border-t border-border flex flex-col gap-2.5">
                    <div className="flex items-center justify-center">
                      <a href="tel:0346386986" className="inline-flex items-center justify-center gap-2 w-full text-xs font-bold text-foreground py-2 px-3 rounded-lg bg-muted/60 border border-border hover:border-secondary/40 transition-colors">
                        <Phone className="size-3.5 text-secondary" />
                        <span className="font-mono">Hotline: 0346 386 986</span>
                      </a>
                    </div>

                    <SheetClose asChild>
                      <Button asChild className="w-full bg-accent text-accent-foreground font-serif font-bold py-2.5 rounded-lg shadow-xs hover:bg-accent/90 transition-all">
                        <Link to="/FreeTrialContactPage">{ctaButtonText}</Link>
                      </Button>
                    </SheetClose>

                    <p className="text-center text-[11px] text-muted-foreground pt-1 font-mono">
                      {lang === "vi" ? "Độ tuổi 4–16 • Robotics, Lập trình & STEM" : "Ages 4–16 • Robotics, Coding & STEM Labs"}
                    </p>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </section>

      {/* Sitewide Floating Contact Cluster - Desktop (Right Edge, Vertically Centered) */}
      <aside aria-label={lang === "vi" ? "Liên hệ nhanh" : "Quick contact"} className="hidden lg:flex fixed right-4 top-1/2 -translate-y-1/2 z-40 flex-col gap-2.5 items-end">
        {/* 1. ĐĂNG KÝ TƯ VẤN */}
        <Button asChild variant="outline" className="h-11 px-3.5 bg-card/95 hover:bg-primary hover:text-primary-foreground text-primary border-primary/50 shadow-md hover:shadow-lg rounded-lg flex items-center gap-2 text-xs font-bold transition-all hover:scale-105">
          <Link to="/FreeTrialContactPage">
            <ClipboardList className="size-4 shrink-0 text-inherit" />
            <span className="leading-tight font-serif tracking-normal">
              {lang === "vi" ? "ĐĂNG KÝ TƯ VẤN" : "CONSULTATION"}
            </span>
          </Link>
        </Button>

        {/* 2. ZALO (disabled / Sắp cập nhật) */}
        <Button type="button" disabled variant="outline" className="h-11 px-3.5 bg-card/90 text-muted-foreground border-border shadow-md rounded-lg flex items-center gap-2 text-xs font-semibold opacity-75 cursor-not-allowed" title={lang === "vi" ? "Zalo (Sắp cập nhật)" : "Zalo (Coming soon)"}>
          <MessageCircle className="size-4 shrink-0 text-[#0068FF]" />
          <span className="flex flex-col items-start leading-tight">
            <span className="font-bold font-mono">ZALO</span>
            <span className="text-[9px] font-normal text-muted-foreground">
              {lang === "vi" ? "Sắp cập nhật" : "Soon"}
            </span>
          </span>
        </Button>

        {/* 3. MESSENGER (disabled / Sắp cập nhật) */}
        <Button type="button" disabled variant="outline" className="h-11 px-3.5 bg-card/90 text-muted-foreground border-border shadow-md rounded-lg flex items-center gap-2 text-xs font-semibold opacity-75 cursor-not-allowed" title={lang === "vi" ? "Messenger (Sắp cập nhật)" : "Messenger (Coming soon)"}>
          <MessageSquare className="size-4 shrink-0 text-[#0084FF]" />
          <span className="flex flex-col items-start leading-tight">
            <span className="font-bold font-mono">MESSENGER</span>
            <span className="text-[9px] font-normal text-muted-foreground">
              {lang === "vi" ? "Sắp cập nhật" : "Soon"}
            </span>
          </span>
        </Button>

        {/* 4. GỌI HOTLINE */}
        <Button asChild variant="secondary" className="h-11 px-3.5 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md hover:shadow-lg rounded-lg flex items-center gap-2 text-xs font-bold transition-all hover:scale-105">
          <a href="tel:0346386986">
            <Phone className="size-4 shrink-0 animate-pulse text-secondary-foreground" />
            <span className="leading-tight font-serif tracking-normal">
              {lang === "vi" ? "GỌI HOTLINE" : "HOTLINE"}
            </span>
          </a>
        </Button>
      </aside>

      {/* Sitewide Floating Contact Bar - Mobile (Compact Bottom Bar with dedicated mobile polish) */}
      <nav aria-label={lang === "vi" ? "Thanh liên hệ nhanh" : "Quick contact bar"} className="lg:hidden fixed bottom-0 inset-x-0 z-40 h-16 bg-background/95 backdrop-blur-xl border-t border-border shadow-xl px-3 flex items-center justify-around gap-1.5">
        {/* 1. GỌI HOTLINE */}
        <Button asChild variant="ghost" size="sm" className="flex-1 h-12 flex-col justify-center items-center gap-0.5 p-1 text-foreground hover:text-secondary hover:bg-secondary/15 rounded-lg transition-colors">
          <a href="tel:0346386986">
            <Phone className="size-4 text-secondary shrink-0" />
            <span className="text-[10px] font-bold tracking-tight">
              {lang === "vi" ? "Hotline" : "Call"}
            </span>
          </a>
        </Button>

        {/* 2. ZALO */}
        <Button type="button" disabled variant="ghost" size="sm" className="flex-1 h-12 flex-col justify-center items-center gap-0.5 p-1 text-muted-foreground opacity-60 rounded-lg cursor-not-allowed" title={lang === "vi" ? "Zalo (Sắp cập nhật)" : "Zalo (Coming soon)"}>
          <MessageCircle className="size-4 text-[#0068FF]" />
          <span className="text-[10px] font-medium tracking-tight">Zalo</span>
        </Button>

        {/* 3. MESSENGER */}
        <Button type="button" disabled variant="ghost" size="sm" className="flex-1 h-12 flex-col justify-center items-center gap-0.5 p-1 text-muted-foreground opacity-60 rounded-lg cursor-not-allowed" title={lang === "vi" ? "Messenger (Sắp cập nhật)" : "Messenger (Coming soon)"}>
          <MessageSquare className="size-4 text-[#0084FF]" />
          <span className="text-[10px] font-medium tracking-tight">Messenger</span>
        </Button>

        {/* 4. ĐĂNG KÝ TƯ VẤN */}
        <Button asChild variant="default" size="sm" className="flex-[1.4] h-11 px-2.5 bg-accent text-accent-foreground hover:bg-accent/90 font-serif font-bold rounded-lg shadow-xs flex items-center justify-center gap-1.5 text-[11px] leading-tight active:scale-[0.98]">
          <Link to="/FreeTrialContactPage">
            <ClipboardList className="size-3.5 shrink-0" />
            <span className="truncate">
              {lang === "vi" ? "Đăng ký tư vấn" : "Consultation"}
            </span>
          </Link>
        </Button>
      </nav>
    </>;
}